import { useGlobal } from "storev2/globalStore";
import {
  isValidEmail,
  isValidPhone,
  isValidName,
  validateIdNumber,
  fetchClientDian,
  isValidAddress,
  splitFullName,
  withDebounce,
  locationToAddress,
} from "tools/helpers";

const getOptions = () => {
  const options = [
    { id: "11", name: "Registro Civil" },
    { id: "12", name: "Tarjeta Identidad" },
    { id: "13", name: "Cédula Ciudadanía" },
    { id: "21", name: "Tarjeta Extranjería" },
    { id: "22", name: "Cédula Extranjería" },
    { id: "31", name: "NIT" },
    { id: "41", name: "Pasaporte" },
    { id: "42", name: "Tipo Documento Extranjero" },
    { id: "47", name: "PEP" },
    { id: "50", name: "NIT Otro País" },
    { id: "91", name: "NUIP" },
  ];
  return options;
};

const getSexOptions = [
  { id: "male", name: "male" },
  { id: "female", name: "female" },
];

const getTypePersonOptions = [
  { id: "persona_natural", name: "Persona Natural" },
  { id: "persona_juridica", name: "Persona Juridica" },
];

const getFiscalRegimenOptions = [
  { id: "48", name: "Impuesto sobre las ventas – IVA" },
  { id: "49", name: "No responsable de IVA" },
]

async function getCountries() {
  const { countries, loadData } = useGlobal.getState();
  if (countries.length === 0) {
    await loadData();
  }
  return useGlobal.getState().countries;
}

async function defaultCountry() {
  const countries = await getCountries();
  return countries.find((c) => c.code === "CO");
}

function isColombia(record) {
  if (!record?.country) return false;
  if (record?.country.code) return record?.country.code === "CO";
  const found = useGlobal.getState().countries.find((x) => x.id === record?.country.id);
  return found?.code === "CO";
}

function getSubdivisions(_, record) {
  if (!record?.country?.id) return null;
  return ["country", "=", record.country.id];
}

function getCities(_, record) {
  if (!record?.subdivision?.id) return null;
  return ["subdivision", "=", record.subdivision.id];
}

function invisibleByCountry(_, record) {
  return !record?.country;
}

function invisibleCityCo(_, record) {
  return !isColombia(record);
}

function invisibleCity(_, record) {
  return isColombia(record);
}


function requiredSubdivisionCo(record) {
  return isColombia(record) && Boolean(record?.street) ;
}
function requiredCityCo(record) {
  return isColombia(record) && Boolean(record?.street) ;
}

function onChangeCountry(_, store) {
  store.active.subdivision = null;
  store.store.subdivision = null;
  store.active.city_co = null;
  store.store.city_co = null;
  store.active.city = null;
  store.store.city = null;
}
function onChangeSubdivision(_, store) {
  const subdivision = store.active?.subdivision ?? store.store?.subdivision;
  if (!subdivision) return;
  store.active.city_co = null;
  store.store.city_co = null;
}

let currentDocType = null;

function layoutParty(showAlert) {
  // const { countries } = useGlobal.getState();
  const ctxView = {
    model: "party.party",
    beforeSave: locationToAddress,
    selectable: "one",
    activeSearch: true,
    filters: null,
    domain: [],
    orderBy: [["id", "DESC"]],
    form_action: ["edit", "add"],
    table_action: ["add", "open"],
    limit: 30,
    fields: {
      name: {
        type: "char",
        searchable: true,
        required: true,
        uppercase: true,
        validate: ({ value }) => {
          if (!value) return true;
          return isValidName(value)
            ? true
            : "party.party.validation.invalid_name";
        },
        withChange: async (_, record) => {
          const name = record.active.name || record.store.name || "";
          splitFullName(name, record);
        },
      },
      first_name: { type: "char", uppercase: true },
      second_name: { type: "char", uppercase: true },
      first_family_name: { type: "char", uppercase: true },
      second_family_name: { type: "char", uppercase: true },
      id_number: {
        type: "char",
        searchable: true,
        required: true,
        validate: ({ value }) => {
          if (!value) return true;
          return validateIdNumber(value, currentDocType)
            ? true
            : "party.party.validation.invalid_IdNumber";
        },
        withChange: withDebounce(async (_, record) => {
          const raw = record.active.id_number ?? record.store.id_number;
          const clean = raw ? String(raw).replace(/[\s.\-]/g, "") : "";

          record.active.id_number = clean;
          record.store.id_number = clean;

          if (!clean || !validateIdNumber(clean, currentDocType)) return;
          const result = await fetchClientDian(record);
          if (result?.error && showAlert) {
            showAlert(result.error?.message || result.error);
          } else if (result?.message && showAlert) {
            showAlert(result.message);
          } else if (!result?.found && showAlert) {
            showAlert("party.party.validation.client_dian");
          }
        }, 500),
      },
      account_payable: { type: "boolean" },
      type_document: {
        type: "selection",
        options: getOptions(),
        required: true,
        withChange: async (_, record) => {
          currentDocType =
            record.active.type_document?.id ?? record.store.type_document?.id;
          const raw = record.active.id_number ?? record.store.id_number;
          const idNumber = raw ? String(raw).replace(/[\s.\-]/g, "") : null;
          if (idNumber && idNumber.length >= 7) {
            const result = await fetchClientDian(record);
            if (result?.error && showAlert) {
              showAlert(result.error?.message || result.error);
            } else if (result?.message && showAlert) {
              showAlert(result.message);
            } else if (!result?.found && showAlert) {
              showAlert("party.party.validation.client_dian");
            }
          }
        },
      },
      sex: {
        type: "selection",
        options: getSexOptions,
        translate: true,
      },
      check_digit: {
        type: "integer",
      },
      main_email: {
        type: "char",
        required: true,
        validate: ({ value }) => {
          if (!value) return true;
          return isValidEmail(value)
            ? true
            : "party.party.validation.invalid_email";
        },
      },
      main_mobile: {
        type: "char",
        required: true,
        validate: ({ value }) => {
          if (!value) return true;
          return isValidPhone(value)
            ? true
            : "party.party.validation.invalid_phone";
        },
      },
      phone: {
        type: "char",
        validate: ({ value }) => {
          if (!value) return true;
          return isValidPhone(value)
            ? true
            : "party.party.validation.invalid_phone";
        },
      },
      birthday: {
        type: "date_2",
      },
      street: {
        type: "char",
        validate: ({ value }) => {
          if (!value) return true;
          return isValidAddress(value);
        },
        // required: true,
        // setter: true,
        // attrs: ["addresses", "addresses.street"],
      },
      country: {
        type: "many2one",
        model: "country.country",
        required: true,
        preOptions: getCountries,
        default: defaultCountry,
        withChange: onChangeCountry,
      },
      subdivision: {
        type: "many2one",
        model: "country.subdivision",
        recSearch: getSubdivisions,
        withChange: onChangeSubdivision,
        required: requiredSubdivisionCo,
        invisible: invisibleByCountry,
      },
      city_co: {
        type: "many2one",
        model: "country.city",
        recSearch: getCities,
        required: requiredCityCo,
        invisible: invisibleCityCo,
      },
      city: {
        type: "char",
        uppercase: true,
        invisible: invisibleCity,
      },
      nationality: {
        type: "many2one",
        model: "country.country",
        preOptions: getCountries,
      },
      commercial_name: {
        type: "char",
        searchable: true,
      },
      type_person: {
        type: "selection",
        options: getTypePersonOptions,
        required: true,
      },
      fiscal_regimen: {
        type: "selection",
        options: getFiscalRegimenOptions,
      }
    },
    list: [
      { name: "name", width: "30%" },
      { name: "id_number", width: "10%" },
      { name: "type_document", width: "15%" },
      { name: "main_mobile", width: "15%" },
      { name: "main_email", width: "15%" },
      { name: "birthday", width: "15%" },
      { name: "commercial_name", width: "10%" },
    ],
    form: [
      { name: "type_document" },
      { name: "id_number" },
      { name: "type_person" },
      { name: "name" },
      { name: "main_email" },
      { name: "main_mobile" },
      { name: "street" },
      { name: "country" },
      { name: "subdivision" },
      { name: "city_co" },
      { name: "city" },
      { name: "nationality" },
      { name: "birthday" },
      { name: "sex" },
      { name: "commercial_name" },
      { name: "fiscal_regimen" },
    ],
  };

  return ctxView;
}

export { layoutParty };
